import { useCallback, useEffect, useMemo, useState } from 'react';

export const useBulkSelection = (allIds: string[]) => {
  const [enabled, setEnabled] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const anySelected = selectedIds.size > 0;

  useEffect(() => {
    const all = new Set(allIds);
    setSelectedIds((current) => {
      if (current.size === 0) {
        return current;
      }

      const next = new Set<string>();

      current.forEach((id) => {
        if (all.has(id)) {
          next.add(id);
        }
      });

      return next;
    });
  }, [allIds]);

  const setSelected = useCallback((ids: string[], value: boolean) =>
    setSelectedIds((current) => {
      const next = new Set(current);

      for (const id of ids) {
        if (value) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }

      return next;
    }), []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const reset = useCallback(() => {
    setEnabled(false);
    setSelectedIds(new Set());
  }, []);

  return useMemo(
    () => ({
      enabled,
      setEnabled,
      selectedIds,
      setSelected,
      clearSelection,
      anySelected,
      reset,
    }),
    [
      enabled,
      selectedIds,
      setSelected,
      clearSelection,
      anySelected,
      reset,
    ]
  );
};
